import { useState } from 'react'
import { MapPin, Pencil, Play, Trash2, Unlink } from 'lucide-react'

import { shortenCameraToken } from './camera-types'
import { CameraStreamView } from './camera-stream-view'
import type { CameraViewMode } from './camera-stream-view'
import type { Camera, CameraStreamState } from './camera-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { m } from '@/lib/paraglide/messages'

interface Props {
    camera: Camera
    /** Tab Camera đang mở và không có chi tiết nào đang mở: mới kéo khung hình. */
    active: boolean
    /**
     * `live` = luồng liên tục (tab của một vùng, 2–3 camera);
     * `snapshot` = ảnh tự làm mới (màn tổng hợp, nhiều camera).
     */
    mode: CameraViewMode
    /** Dòng vị trí phía trên tên camera, vd "PHẠM TIẾN ĐẠT (BÁC ĐẠT) · Bãi Anh Hà". */
    location?: string
    canWrite: boolean
    /** Không truyền thì thẻ không mở xem trực tiếp (vd lưới trong hộp thoại). */
    onLive?: (camera: Camera) => void
    onEdit: (camera: Camera) => void
    /**
     * Nút thùng rác: `delete` xoá hẳn camera (màn quản lý camera), `unplace`
     * chỉ gỡ camera khỏi vùng đang xem (tab camera của vùng) — camera vẫn còn
     * để gắn chỗ khác.
     */
    onRemove: (camera: Camera) => void
    removeMode?: 'delete' | 'unplace'
    /** Nhãn nút gỡ thay cho "Gỡ khỏi vùng" (vd "Gỡ khỏi trạm"). */
    removeLabel?: string
    /** Có thì hiện nút gắn/đổi vùng (màn quản lý camera). */
    onPlace?: (camera: Camera) => void
}

/**
 * Một camera trong lưới: khung hình, trạng thái, và các nút xem/sửa/gắn vùng/
 * xoá (hoặc gỡ khỏi vùng).
 *
 * Trạng thái lấy từ chính thẻ ảnh (`playing`/`error`), không từ một cột DB —
 * camera ngoài vườn rụng mạng liên tục, một cột "online" lưu sẵn sẽ luôn sai.
 */
export function CameraCard({
    camera,
    active,
    mode,
    location,
    canWrite,
    onLive,
    onEdit,
    onRemove,
    removeMode = 'delete',
    removeLabel,
    onPlace,
}: Props) {
    const [streamState, setStreamState] =
        useState<CameraStreamState>('unconfigured')

    return (
        <article className="flex flex-col overflow-hidden rounded-md border bg-card">
            <div className="relative aspect-video w-full max-w-full">
                <CameraStreamView
                    camera={camera}
                    active={active}
                    mode={mode}
                    onStateChange={setStreamState}
                    className="size-full"
                />
                <StatusBadge state={streamState} />
                {camera.has_stream && onLive && (
                    <button
                        type="button"
                        aria-label={m.grower_area_camera_live_label({
                            name: camera.name,
                        })}
                        onClick={() => onLive(camera)}
                        // Máy tính: nút mờ, rê chuột mới rõ cho đỡ che khung
                        // hình. Cảm ứng không có rê chuột nên phải hiện sẵn.
                        className="absolute inset-0 flex cursor-pointer items-center justify-center opacity-100 transition-opacity focus-visible:outline-none md:opacity-0 md:hover:opacity-100 md:focus-visible:opacity-100"
                    >
                        <span className="inline-flex items-center gap-1.5 rounded-md bg-black/70 px-3 py-1.5 text-xs font-medium text-white">
                            <Play className="size-3.5" />
                            {m.grower_area_camera_live()}
                        </span>
                    </button>
                )}
            </div>

            <div className="flex flex-1 flex-col gap-1.5 px-2.5 py-2">
                {location && (
                    <span
                        className="truncate text-xs font-medium text-muted-foreground"
                        title={location}
                    >
                        {location}
                    </span>
                )}
                <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">
                        {camera.name}
                    </span>
                    <Badge
                        variant={camera.can_control ? 'secondary' : 'outline'}
                    >
                        {camera.can_control
                            ? m.grower_area_camera_badge_ptz()
                            : m.grower_area_camera_badge_view_only()}
                    </Badge>
                </div>
                <span
                    className="truncate font-mono text-[11px] text-muted-foreground"
                    title={shortenCameraToken(camera.stream_token)}
                >
                    {camera.has_stream
                        ? shortenCameraToken(camera.stream_token)
                        : m.grower_area_camera_missing_stream()}
                </span>

                {canWrite && (
                    <div className="mt-auto flex gap-1.5 pt-1">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => onEdit(camera)}
                        >
                            <Pencil className="size-3.5" />
                            {camera.has_stream
                                ? m.grower_area_camera_edit()
                                : m.grower_area_camera_fill_paths()}
                        </Button>
                        {onPlace && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => onPlace(camera)}
                            >
                                <MapPin className="size-3.5" />
                                {camera.grower_area
                                    ? m.camera_location_change()
                                    : m.camera_location_place()}
                            </Button>
                        )}
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            aria-label={
                                removeMode === 'unplace'
                                    ? (removeLabel ??
                                      m.camera_location_remove())
                                    : m.grower_area_camera_delete()
                            }
                            title={
                                removeMode === 'unplace'
                                    ? (removeLabel ??
                                      m.camera_location_remove())
                                    : m.grower_area_camera_delete()
                            }
                            onClick={() => onRemove(camera)}
                        >
                            {removeMode === 'unplace' ? (
                                <Unlink className="size-3.5" />
                            ) : (
                                <Trash2 className="size-3.5" />
                            )}
                        </Button>
                    </div>
                )}
            </div>
        </article>
    )
}

function StatusBadge({ state }: { state: CameraStreamState }) {
    const label =
        state === 'playing'
            ? m.grower_area_camera_online()
            : state === 'error'
              ? m.grower_area_camera_offline()
              : state === 'unconfigured'
                ? m.grower_area_camera_unconfigured()
                : state === 'paused'
                  ? m.grower_area_camera_paused()
                  : m.common_loading()

    const dot =
        state === 'playing'
            ? 'bg-emerald-400'
            : state === 'error'
              ? 'bg-red-400'
              : state === 'unconfigured'
                ? 'bg-amber-400'
                : state === 'paused'
                  ? 'bg-sky-300'
                  : 'bg-neutral-300'

    return (
        <span className="absolute left-2 top-2 inline-flex items-center gap-1.5 rounded-md bg-black/55 px-2 py-1 text-[11px] font-medium text-white">
            <span aria-hidden className={`size-2 rounded-full ${dot}`} />
            {label}
        </span>
    )
}
